import { ChevronRight, Download, FileText, Search, SearchX } from 'lucide-react'
import { useQueryState } from 'nuqs'
import { ReactNode, useMemo, useState } from 'react'
import { Link } from 'react-router'
import useSWR from 'swr'

import { Money, PageHeader, SegmentedTabs, StatStrip, StatusBadge } from '@/components/ds'
import { EmptyState } from '@/components/EmptyState'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useSetPageHeader } from '@/contexts/PageHeaderContext'
import { apiFetcher } from '@/data/fetcher'
import { useRoleCheck } from '@/hooks/useRoleCheck'
import { Settings } from '@/interfaces/Settings'
import API from '@/lib/api'
import { downloadFile } from '@/lib/downloadFile'
import { cn } from '@/lib/utils'
import { Role } from '@/types/User'

import { DocumentSheet } from './DocumentSheet'
import {
  counterpartyOf,
  displayStatus,
  Doc,
  docCurrency,
  DocKind,
  docsCount,
  docTotal,
  formatShortDate,
  kindMeta,
  relativeDue,
  StatusGroup,
  statusGroup,
  statusLabelFor,
} from './documentUtils'

const MONTHS = ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec']
const PAGE = 25

/** Sdílený seznam dokladů: souhrny, filtry stavů, hledání, tabulka a detail v panelu. */
export const DocumentListView = ({ kind, aside }: { kind: DocKind; aside?: (period: { year: string; month: string | null }) => ReactNode }) => {
  const meta = kindMeta[kind]
  const currentYear = new Date().getFullYear()
  const [yearParam, setYear] = useQueryState('year')
  const [monthParam, setMonth] = useQueryState('month')
  const [statusParam, setStatus] = useQueryState('status')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(0)
  // otevřený detail v URL (?doc=číslo): sdílitelný odkaz, přežije obnovení, Zpět ho zavře
  const [docParam, setDocParam] = useQueryState('doc', { history: 'push' })
  const canCreate = useRoleCheck([Role.owner, Role.manager, Role.employee])

  const year = yearParam || String(currentYear)
  const status = (statusParam as StatusGroup) || 'all'

  const { data: settings } = useSWR<Settings>('/api/v2/settings', apiFetcher)
  const url = `/api/v2/${meta.api}?year=${year}${monthParam ? `&month=${monthParam}` : ''}&limit=1000`
  const { data, isLoading, mutate } = useSWR<Doc[]>(url, apiFetcher, { keepPreviousData: true })

  useSetPageHeader({
    breadcrumbs: [{ href: `/${meta.api}`, children: meta.plural }],
    newButton: kind === 'expense' ? 'expense' : kind,
  })

  const docs = useMemo(() => data ?? [], [data])

  const inList = docParam ? docs.find((d) => d.symbol === docParam) : undefined
  // doklad mimo načtené období (sdílený odkaz) dotáhneme zvlášť
  const { data: fetchedDoc, mutate: mutateDoc } = useSWR<Doc>(
    docParam && !inList && !isLoading ? `/api/v2/${meta.api}/${encodeURIComponent(docParam)}` : null,
    apiFetcher,
  )
  const selected = inList ?? fetchedDoc ?? null

  const groups = useMemo(() => {
    type Bucket = { count: number; total: number; foreign: Record<string, { count: number; total: number }> }
    const empty = (): Bucket => ({ count: 0, total: 0, foreign: {} })
    const acc: Record<Exclude<StatusGroup, 'all'>, Bucket> = { open: empty(), overdue: empty(), paid: empty(), cancelled: empty() }
    const primaryCurrency = settings?.primaryCurrency ?? 'CZK'
    for (const d of docs) {
      const g = statusGroup(d)
      const cur = docCurrency(d)
      acc[g].count += 1
      // bez přepočtu kurzem: hlavní měna v částce, cizí měny zvlášť pod ní
      if (cur === primaryCurrency) acc[g].total += docTotal(d)
      else {
        acc[g].foreign[cur] ??= { count: 0, total: 0 }
        acc[g].foreign[cur].count += 1
        acc[g].foreign[cur].total += docTotal(d)
      }
    }
    return acc
  }, [docs, settings?.primaryCurrency])

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    return docs
      .filter((d) => status === 'all' || statusGroup(d) === status)
      .filter((d) => {
        if (!q) return true
        const cp = counterpartyOf(d, kind)
        return [d.symbol, cp?.name, cp?.ico].some((v) => v?.toLowerCase().includes(q))
      })
  }, [docs, status, query, kind])

  const pageDocs = filtered.slice(page * PAGE, (page + 1) * PAGE)
  const pages = Math.max(1, Math.ceil(filtered.length / PAGE))
  const primary = settings?.primaryCurrency ?? 'CZK'

  const years = []
  for (let y = currentYear; y >= (settings?.yearOfRegistration ?? currentYear - 5); y--) years.push(String(y))

  const exportPeriod = async (withMonth: boolean) => {
    const params = `year=${year}${withMonth && monthParam ? `&month=${monthParam}` : ''}`
    const response = await API.get(`/api/v2/export/${meta.api}?${params}`, { responseType: 'blob', withCredentials: true })
    downloadFile(response)
  }

  const setFilter = (value: StatusGroup) => {
    setStatus(value === 'all' ? null : value)
    setPage(0)
  }

  const openLabel = kind === 'expense' ? 'K úhradě' : 'Neuhrazené'

  const hintFor = (g: Exclude<StatusGroup, 'all'>) => {
    const b = groups[g]
    const foreign = Object.entries(b.foreign)
    return (
      <span className="flex flex-wrap items-baseline gap-x-1.5">
        <span>{docsCount(b.count)}</span>
        {foreign.map(([cur, f]) => (
          <span key={cur} className="inline-flex items-baseline gap-1">
            + <Money amount={f.total} currency={cur} size="sm" className="font-normal text-foreground" /> ({f.count})
          </span>
        ))}
      </span>
    )
  }

  return (
    <>
      <title>{meta.plural}</title>
      <div className="mx-auto w-full max-w-[1280px]">
        <PageHeader
          title={meta.plural}
          description={`${monthParam ? MONTHS[Number(monthParam) - 1] + ' ' : ''}${year}, ${docsCount(docs.length)}`}
          actions={
            <>
              <Select value={year} onValueChange={(v) => { setYear(v === String(currentYear) ? null : v); setPage(0) }}>
                <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                <SelectContent>{years.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
              </Select>
              <Select value={monthParam ?? 'all'} onValueChange={(v) => { setMonth(v === 'all' ? null : v); setPage(0) }}>
                <SelectTrigger className="w-36"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Celý rok</SelectItem>
                  {MONTHS.map((m, i) => <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>)}
                </SelectContent>
              </Select>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="icon" aria-label="Export"><Download /></Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {monthParam && <DropdownMenuItem onClick={() => exportPeriod(true)}>Export za měsíc</DropdownMenuItem>}
                  <DropdownMenuItem onClick={() => exportPeriod(false)}>Export za rok {year}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          }
        />

        <StatStrip
          className="mb-6"
          items={[
            { label: openLabel, value: <Money amount={groups.open.total} currency={primary} size="lg" animate />, hint: hintFor('open'), active: status === 'open', onClick: () => setFilter(status === 'open' ? 'all' : 'open') },
            { label: 'Po splatnosti', value: <Money amount={groups.overdue.total} currency={primary} size="lg" animate className={groups.overdue.count ? 'text-danger' : ''} />, hint: hintFor('overdue'), active: status === 'overdue', onClick: () => setFilter(status === 'overdue' ? 'all' : 'overdue') },
            { label: 'Zaplaceno', value: <Money amount={groups.paid.total} currency={primary} size="lg" animate />, hint: hintFor('paid'), active: status === 'paid', onClick: () => setFilter(status === 'paid' ? 'all' : 'paid') },
          ]}
        />

        {aside?.({ year, month: monthParam })}

        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <SegmentedTabs<StatusGroup>
            value={status}
            onChange={setFilter}
            options={[
              { value: 'all', label: 'Vše', count: docs.length },
              { value: 'open', label: openLabel, count: groups.open.count },
              { value: 'overdue', label: 'Po splatnosti', count: groups.overdue.count },
              { value: 'paid', label: 'Zaplacené', count: groups.paid.count },
              ...(groups.cancelled.count ? [{ value: 'cancelled' as const, label: 'Zrušené', count: groups.cancelled.count }] : []),
            ]}
          />
          <div className="relative w-full sm:w-72">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={query} onChange={(e) => { setQuery(e.target.value); setPage(0) }} placeholder="Číslo, firma nebo IČO" className="pl-8" />
          </div>
        </div>

        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="hidden w-40 md:table-cell">Číslo</TableHead>
                <TableHead>{meta.counterpartyLabel}</TableHead>
                <TableHead className="hidden w-48 md:table-cell">Splatnost</TableHead>
                <TableHead className="text-right md:w-40">Částka</TableHead>
                <TableHead className="hidden w-36 md:table-cell">Stav</TableHead>
                <TableHead className="hidden w-10 md:table-cell" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading &&
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 6 }).map((__, j) => (
                      <TableCell key={j} className={j === 1 || j === 3 ? undefined : 'hidden md:table-cell'}><Skeleton className="h-4 w-full max-w-32" /></TableCell>
                    ))}
                  </TableRow>
                ))}
              {!isLoading &&
                pageDocs.map((d) => {
                  const cp = counterpartyOf(d, kind)
                  const st = displayStatus(d)
                  const due = statusGroup(d) === 'paid' || statusGroup(d) === 'cancelled' ? null : relativeDue(d.deadlineAt)
                  return (
                    <TableRow key={d.uuid} onClick={() => setDocParam(d.symbol ?? null)} className="group cursor-pointer">
                      <TableCell className="hidden md:table-cell">
                        <p className="font-medium">{d.symbol}</p>
                        <p className="text-xs text-muted-foreground">{formatShortDate(d.issuedAt)}</p>
                      </TableCell>
                      <TableCell className="w-full max-w-0 md:w-auto">
                        <p className="truncate font-medium md:font-normal">{cp?.name}</p>
                        {cp?.ico && <p className="hidden text-xs text-muted-foreground md:block">IČO {cp.ico}</p>}
                        <p className="truncate text-xs text-muted-foreground md:hidden">
                          {d.symbol}
                          {due ? (
                            <span className={cn(due.late && 'text-danger')}>, {due.text}</span>
                          ) : (
                            `, ${formatShortDate(d.issuedAt)}`
                          )}
                        </p>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <p>{formatShortDate(d.deadlineAt)}</p>
                        {due && <p className={cn('text-xs', due.late ? 'text-danger' : 'text-muted-foreground')}>{due.text}</p>}
                        {statusGroup(d) === 'paid' && d.paidAt && <p className="text-xs text-muted-foreground">zaplaceno {formatShortDate(d.paidAt)}</p>}
                      </TableCell>
                      <TableCell className="text-right">
                        <Money amount={docTotal(d)} currency={docCurrency(d)} />
                        <div className="mt-0.5 md:hidden">
                          <StatusBadge status={st} label={statusLabelFor(kind, st)} />
                        </div>
                      </TableCell>
                      <TableCell className="hidden md:table-cell"><StatusBadge status={st} label={statusLabelFor(kind, st)} /></TableCell>
                      <TableCell className="hidden text-right md:table-cell">
                        <ChevronRight className="ml-auto size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100" />
                      </TableCell>
                    </TableRow>
                  )
                })}
            </TableBody>
          </Table>

          {!isLoading && filtered.length === 0 && (
            <div className="p-6">
              <EmptyState icon={docs.length ? SearchX : FileText}
                title={docs.length ? 'Žádné doklady neodpovídají filtru' : 'V tomto období zatím nic není'}
                description={docs.length ? 'Zkuste jiný stav nebo hledaný výraz.' : undefined}
                ctaButton={
                  !docs.length && canCreate ? (
                    <Button asChild><Link to={kind === 'expense' ? '/expense/upload' : `/${meta.segment}/new`}>Vytvořit</Link></Button>
                  ) : undefined
                }
              />
            </div>
          )}

          {filtered.length > PAGE && (
            <div className="flex items-center justify-between border-t px-3 py-2 text-xs text-muted-foreground">
              <span className="tabular">
                {page * PAGE + 1}-{Math.min((page + 1) * PAGE, filtered.length)} z {filtered.length}
              </span>
              <div className="flex gap-1">
                <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Předchozí</Button>
                <Button variant="outline" size="sm" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}>Další</Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <DocumentSheet
        kind={kind}
        doc={selected}
        onOpenChange={(o) => !o && setDocParam(null)}
        onChanged={async () => {
          await Promise.all([mutate(), mutateDoc()])
        }}
      />
    </>
  )
}
